/*
 *
 * Hedera Stablecoin SDK
 *
 * Copyright (C) 2023 Hedera Hashgraph, LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
import { Transaction, Client, } from '@hashgraph/sdk';
import { singleton } from 'tsyringe';
import { HederaTransactionAdapter } from '../HederaTransactionAdapter.js';
import { HTSTransactionResponseAdapter } from './HTSTransactionResponseAdapter.js';
import Injectable from '../../../../core/Injectable.js';
import { WalletEvents, } from '../../../../app/service/event/WalletEvent.js';
import { SupportedWallets } from '../../../in/request/ConnectRequest.js';
import EventService from '../../../../app/service/event/EventService.js';
import { lazyInject } from '../../../../core/decorator/LazyInjectDecorator.js';
import { MirrorNodeAdapter } from '../../mirror/MirrorNodeAdapter.js';
import NetworkService from '../../../../app/service/NetworkService.js';
import LogService from '../../../../app/service/LogService.js';
import { WalletConnectError } from '../../../../domain/context/network/error/WalletConnectError.js';
import { SigningError } from '../error/SigningError.js';
import Hex from '../../../../core/Hex.js';
let HTSTransactionAdapter = class HTSTransactionAdapter extends HederaTransactionAdapter {
    get client() {
        return this._client;
    }
    constructor(eventService, mirrorNodeAdapter, networkService) {
        super(mirrorNodeAdapter, networkService);
        this.eventService = eventService;
        this.mirrorNodeAdapter = mirrorNodeAdapter;
        this.networkService = networkService;
    }
    init() {
        this.eventService.emit(WalletEvents.walletInit, {
            wallet: SupportedWallets.CLIENT,
            initData: {},
        });
        LogService.logTrace('Client Initialized');
        return Promise.resolve(this.networkService.environment);
    }
    async register(account) {
        Injectable.registerTransactionHandler(this);
        const accountMirror = await this.mirrorNodeAdapter.getAccountInfo(account.id);
        this.account = account;
        this.account.publicKey = accountMirror.publicKey;
        this.network = this.networkService.environment;
        this._client = Client.forName(this.networkService.environment);
        const id = this.account.id?.value ?? '';
        if (!account.privateKey)
            throw new WalletConnectError('A private key is needed for the account');
        const privateKey = account.privateKey.toHashgraphKey();
        this._client.setOperator(id, privateKey);
        const eventData = {
            wallet: SupportedWallets.CLIENT,
            data: {
                account: this.account,
                pairing: '',
                topic: '',
            },
            network: {
                name: this.networkService.environment,
                recognized: true,
                factoryId: this.networkService.configuration
                    ? this.networkService.configuration.factoryAddress
                    : '',
            },
        };
        this.eventService.emit(WalletEvents.walletPaired, eventData);
        LogService.logTrace('Client registered as handler: ', eventData);
        return Promise.resolve({
            account: this.getAccount(),
        });
    }
    stop() {
        this.client?.close();
        LogService.logTrace('Client stopped');
        this.eventService.emit(WalletEvents.walletDisconnect, {
            wallet: SupportedWallets.CLIENT,
        });
        return Promise.resolve(true);
    }
    async signAndSendTransaction(t, transactionType, functionName, abi) {
        const tr = await t.execute(this.client);
        this.logTransaction(tr.transactionId.toString(), this.networkService.environment);
        return HTSTransactionResponseAdapter.manageResponse(this.networkService.environment, tr, transactionType, this.client, functionName, abi);
    }
    getAccount() {
        return this.account;
    }
    async sign(message) {
        if (!this.account.privateKey)
            throw new SigningError('Private Key is empty');
        if (!(message instanceof Transaction))
            throw new SigningError('HTS must sign a transaction not a string');
        try {
            const signer = this.account.privateKey.toHashgraphKey();
            const signed_Transaction = await signer.signTransaction(message);
            return Hex.fromUint8Array(signed_Transaction);
        }
        catch (error) {
            LogService.logError(error);
            throw new SigningError(error);
        }
    }
};
HTSTransactionAdapter = __decorate([
    singleton(),
    __param(0, lazyInject(EventService)),
    __param(1, lazyInject(MirrorNodeAdapter)),
    __param(2, lazyInject(NetworkService)),
    __metadata("design:paramtypes", [EventService,
        MirrorNodeAdapter,
        NetworkService])
], HTSTransactionAdapter);
export { HTSTransactionAdapter };
